import { Button, MenuItem, MenuList } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Square as OsanoLogoSquare } from '/b2b/common/icons/Osano';
import PropTypes from 'prop-types';
import SidebarFlyout from '../SidebarFlyout';
import { makeStyles } from 'tss-react/mui';
import noop from 'lodash/noop';
import styles from './OrgSwitcher.styles';
import { translate } from '/b2b/common/helpers/i18n';

const useStyles = makeStyles()(styles);

const OrgSwitcher = props => {
    const {
        collapsed,
        currentCustomerId,
        customerName = '',
        customers = [],
        onChangeCustomer = noop,
        onFlyoutEnter = noop,
        onFlyoutLeave = noop,
    } = props;
    const { classes, cx } = useStyles();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    // When the rail collapses out from under an open menu, close the menu so it
    // doesn't linger as a detached popover.
    useEffect(() => {
        if (collapsed) {
            setOpen(false);
        }
    }, [collapsed]);

    if (customers.length <= 1) {
        return null;
    }

    const handleSelect = customerId => {
        setOpen(false);
        onChangeCustomer(customerId);
    };

    return (
        <div className={cx(classes.root, { [classes.collapsed]: collapsed })}>
            {/* One inset card in every state: the mark holds the icon centerline
                while the brand, org name, and chevron are hidden on the collapsed
                rail and slide in as it expands — matching the nav rows below. */}
            <Button
                className={classes.trigger}
                ref={anchorRef}
                aria-haspopup
                aria-expanded={open}
                aria-label={translate('common.switchOrganization')}
                onClick={() => setOpen(prevOpen => !prevOpen)}
            >
                <OsanoLogoSquare className={classes.triggerLogo} alt="Osano logo" />
                <span className={classes.triggerText}>
                    <span className={classes.triggerBrand}>Osano</span>
                    <span className={classes.triggerLabel}>{customerName}</span>
                </span>
                <ArrowDropDownIcon className={classes.triggerChevron} />
            </Button>
            <SidebarFlyout
                anchorEl={anchorRef.current}
                open={open}
                onClose={() => setOpen(false)}
                onMouseEnter={onFlyoutEnter}
                onMouseLeave={onFlyoutLeave}
                placement="bottom-start"
            >
                <MenuList className={classes.menuList}>
                    {customers.map(({ customerId, name }) =>
                        customerId === '' ? null : (
                            <MenuItem
                                key={customerId}
                                className={classes.menuItem}
                                value={customerId}
                                selected={customerId === currentCustomerId}
                                disabled={customerId === currentCustomerId}
                                onClick={() => handleSelect(customerId)}
                            >
                                {name}
                            </MenuItem>
                        )
                    )}
                </MenuList>
            </SidebarFlyout>
        </div>
    );
};

OrgSwitcher.propTypes = {
    collapsed: PropTypes.bool,
    currentCustomerId: PropTypes.string,
    customerName: PropTypes.string,
    customers: PropTypes.array,
    onChangeCustomer: PropTypes.func,
    onFlyoutEnter: PropTypes.func,
    onFlyoutLeave: PropTypes.func,
};

export default OrgSwitcher;
